// src/features/search/IndexDetailPanel.tsx
// Admin detail rail for a single search index: config + field weights,
// stats, reindex, delete, and a small set of manual ops tools (index one
// document, bulk index, remove a document) for troubleshooting when the
// normal Kafka-driven indexing pipeline needs a manual nudge.

import { useEffect, useState, useCallback } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { searchStore } from '@core/stores/search/SearchStore';
import { searchApi } from '@core/api';
import { bytesToHuman, parseDocumentsJSON } from './logic';
import { errMessage, fmtDateTime } from './format';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { SearchIndex, GetIndexStatsResponse, IndexFieldConfig } from '@core/types/search';

export interface IndexDetailPanelProps {
  indexId: string;
  onClose: () => void;
  onDeleted: (id: string) => void;
  onUpdated: (index: SearchIndex) => void;
}

type Tab = 'config' | 'stats' | 'ops';

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

export function IndexDetailPanel({ indexId, onClose, onDeleted, onUpdated }: IndexDetailPanelProps) {
  const { t, locale } = useI18n();
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('config');

  // Editable config
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState<IndexFieldConfig[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [stats, setStats] = useState<GetIndexStatsResponse | null>(null);
  const [isReindexing, setIsReindexing] = useState(false);

  // Ops tools
  const [singleDocJson, setSingleDocJson] = useState('');
  const [isIndexingSingle, setIsIndexingSingle] = useState(false);
  const [bulkDocsJson, setBulkDocsJson] = useState('');
  const [isBulkIndexing, setIsBulkIndexing] = useState(false);
  const [removeUrn, setRemoveUrn] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [opsMessage, setOpsMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    searchApi.GetSearchIndex(indexId)
      .then((res) => {
        const idx = res.index ?? null;
        setIndex(idx);
        if (idx) {
          setName(idx.name);
          setDescription(idx.description ?? '');
          setIsActive(idx.is_active);
          setFields(idx.index_fields ?? []);
        }
      })
      .catch((e) => setError(errMessage(e, t('search.admin.loadError'))))
      .finally(() => setIsLoading(false));
  }, [indexId, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'stats') return;
    searchStore.GetIndexStats(indexId).then(setStats).catch(() => {});
  }, [tab, indexId]);

  const handleSave = async () => {
    if (!index) return;
    setIsSaving(true);
    try {
      const updated = await searchStore.UpdateSearchIndex(indexId, {
        index: { ...index, name, description, is_active: isActive, index_fields: fields },
      });
      setIndex(updated);
      onUpdated(updated);
    } catch (e) {
      setError(errMessage(e, t('search.admin.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await searchStore.DeleteSearchIndex(indexId);
      onDeleted(indexId);
      onClose();
    } catch (e) {
      setError(errMessage(e, t('search.admin.deleteError')));
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleReindex = async () => {
    setIsReindexing(true);
    try {
      await searchStore.ReIndex(indexId, { force: true });
      setOpsMessage(t('search.admin.reindexQueued'));
    } catch (e) {
      setError(errMessage(e, t('search.admin.reindexError')));
    } finally {
      setIsReindexing(false);
    }
  };

  const updateFieldWeight = (fieldName: string, weight: number) => {
    setFields((prev) => prev.map((f) => (f.field_name === fieldName ? { ...f, weight } : f)));
  };

  const handleIndexSingle = async () => {
    setIsIndexingSingle(true);
    setOpsMessage(null);
    try {
      const docs = parseDocumentsJSON(singleDocJson);
      await searchStore.IndexDocument({ document: docs[0] as any });
      setOpsMessage(t('search.admin.ops.indexedOne'));
      setSingleDocJson('');
    } catch (e) {
      setError(errMessage(e, t('search.admin.ops.indexError')));
    } finally {
      setIsIndexingSingle(false);
    }
  };

  const handleBulkIndex = async () => {
    setIsBulkIndexing(true);
    setOpsMessage(null);
    try {
      const docs = parseDocumentsJSON(bulkDocsJson);
      const res = await searchStore.BulkIndexDocuments({
        documents: docs as any,
        product_slug: index?.product_slug ?? '',
      });
      setOpsMessage(t('search.admin.ops.bulkResult', { indexed: res.indexed, updated: res.updated, failed: res.failed }));
      setBulkDocsJson('');
    } catch (e) {
      setError(errMessage(e, t('search.admin.ops.indexError')));
    } finally {
      setIsBulkIndexing(false);
    }
  };

  const handleRemoveDocument = async () => {
    if (!removeUrn.trim()) return;
    setIsRemoving(true);
    setOpsMessage(null);
    try {
      await searchStore.DeleteDocument(removeUrn.trim());
      setOpsMessage(t('search.admin.ops.removed'));
      setRemoveUrn('');
    } catch (e) {
      setError(errMessage(e, t('search.admin.ops.removeError')));
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <aside
      aria-label={t('search.admin.detailLabel')}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-background shadow-xl xl:relative xl:inset-auto xl:shadow-none"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="truncate text-sm font-semibold text-foreground">{index?.name ?? t('search.admin.detailLabel')}</h2>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t('common.close')}>
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : (
        <>
          <div className="flex border-b border-border px-4">
            {(['config', 'stats', 'ops'] as Tab[]).map((tb) => (
              <button
                key={tb}
                onClick={() => setTab(tb)}
                className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  tab === tb ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`search.admin.tab.${tb}`)}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
            {opsMessage && <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">{opsMessage}</div>}

            {tab === 'config' && index && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground" htmlFor="idx-name">{t('search.admin.field.name')}</label>
                  <input id="idx-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground" htmlFor="idx-desc">{t('search.admin.field.description')}</label>
                  <textarea id="idx-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-input" />
                  {t('search.admin.field.active')}
                </label>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('search.admin.field.fieldWeights')}</p>
                  <ul className="mt-1 space-y-1.5">
                    {fields.map((f) => (
                      <li key={f.field_name} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate text-foreground">{f.field_name}</span>
                        <input
                          type="number"
                          step="0.1"
                          value={f.weight}
                          onChange={(e) => updateFieldWeight(f.field_name, e.target.value === '' ? 0 : Number(e.target.value))}
                          className="w-20 rounded-md border border-input bg-background px-2 py-1 text-right text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </li>
                    ))}
                    {fields.length === 0 && <li className="text-xs text-muted-foreground">{t('search.admin.field.noFields')}</li>}
                  </ul>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <Button size="sm" onClick={handleSave} isLoading={isSaving}>{t('common.save')}</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(true)}>{t('common.delete')}</Button>
                </div>
              </div>
            )}

            {tab === 'stats' && (
              <div className="space-y-3">
                {!stats ? (
                  <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-md border border-border p-3">
                        <p className="text-xs text-muted-foreground">{t('search.admin.stats.documentCount')}</p>
                        <p className="text-lg font-semibold text-foreground">{stats.document_count}</p>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <p className="text-xs text-muted-foreground">{t('search.admin.stats.indexSize')}</p>
                        <p className="text-lg font-semibold text-foreground">{bytesToHuman(stats.index_size_bytes)}</p>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <p className="text-xs text-muted-foreground">{t('search.admin.stats.pending')}</p>
                        <p className="text-lg font-semibold text-foreground">{stats.pending_documents}</p>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <p className="text-xs text-muted-foreground">{t('search.admin.stats.lastReindex')}</p>
                        <p className="text-sm text-foreground">{fmtDateTime(stats.last_reindex_at, locale)}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="secondary" onClick={handleReindex} isLoading={isReindexing}>
                      {t('search.admin.reindexNow')}
                    </Button>
                  </>
                )}
              </div>
            )}

            {tab === 'ops' && (
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('search.admin.ops.indexOne')}</p>
                  <textarea
                    value={singleDocJson}
                    onChange={(e) => setSingleDocJson(e.target.value)}
                    rows={4}
                    placeholder='{"entity_urn": "...", "product_slug": "...", "title": "..."}'
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button size="sm" variant="secondary" onClick={handleIndexSingle} isLoading={isIndexingSingle} disabled={!singleDocJson.trim()}>
                    {t('search.admin.ops.indexOneButton')}
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('search.admin.ops.bulkIndex')}</p>
                  <textarea
                    value={bulkDocsJson}
                    onChange={(e) => setBulkDocsJson(e.target.value)}
                    rows={5}
                    placeholder='[{"entity_urn": "...", "product_slug": "..."}, ...]'
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button size="sm" variant="secondary" onClick={handleBulkIndex} isLoading={isBulkIndexing} disabled={!bulkDocsJson.trim()}>
                    {t('search.admin.ops.bulkIndexButton')}
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('search.admin.ops.removeDocument')}</p>
                  <input
                    value={removeUrn}
                    onChange={(e) => setRemoveUrn(e.target.value)}
                    placeholder="urn:corde:…"
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={handleRemoveDocument} isLoading={isRemoving} disabled={!removeUrn.trim()}>
                    {t('search.admin.ops.removeButton')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('search.admin.deleteConfirm')}
        destructive
        isConfirming={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </aside>
  );
}
